import { supabase } from './supabase'

/**
 * Check if current user is a master admin
 * Uses RPC function to avoid RLS issues and 406 errors
 */
export const isMasterAdmin = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return false

    // Use RPC function to check master admin status (bypasses RLS issues)
    const { data, error } = await supabase.rpc('check_master_admin', {
      user_id_param: user.id
    })

    if (error) {
      // If RPC doesn't exist, fall back to direct query but handle errors gracefully
      if (error.code === '42883') {
        // Function doesn't exist, use direct query
        const { data: directData, error: directError } = await supabase
          .from('master_admins')
          .select('id')
          .eq('user_id', user.id)
          .eq('is_active', true)
          .maybeSingle()

        if (directError && directError.code !== 'PGRST116') {
          // Only log if it's not a "not found" error
          console.warn('Error checking master admin (direct query):', directError)
        }
        return !!directData
      }
      console.warn('Error checking master admin:', error)
      return false
    }

    return !!data
  } catch (error) {
    // Silently fail - don't log errors for non-master-admin users
    return false
  }
}

/**
 * Get all organizations with subscription details
 */
export const getAllOrganizations = async () => {
  try {
    const { data, error } = await supabase
      .from('organizations')
      .select(`
        *,
        subscriptions (*),
        organization_members (count),
        clients:clients (count)
      `)
      .order('created_at', { ascending: false })

    if (error) throw error
    return data || []
  } catch (error) {
    console.error('Error fetching organizations:', error)
    throw error
  }
}

/**
 * Get organization details with full user information
 */
export const getOrganizationDetails = async (organizationId) => {
  try {
    // Get organization with members
    const { data, error } = await supabase
      .from('organizations')
      .select(`
        *,
        subscriptions (*),
        organization_members (*),
        clients (count)
      `)
      .eq('id', organizationId)
      .single()

    if (error) throw error

    // Get user details for all members (master admin only)
    if (data.organization_members && data.organization_members.length > 0) {
      const userIds = data.organization_members.map(m => m.user_id).filter(Boolean)
      
      if (userIds.length > 0) {
        try {
          const { data: userDetails, error: userError } = await supabase.rpc('get_user_details', {
            user_ids: userIds
          })

          if (userError) {
            // If RPC doesn't exist (42883 = function does not exist), log and continue
            if (userError.code === '42883') {
              console.warn('get_user_details RPC function not found. Please run supabase-master-admin-user-details.sql migration.')
            } else {
              console.warn('Error fetching user details:', userError)
            }
          } else if (userDetails) {
            // Map user details to members
            const userDetailsMap = new Map(userDetails.map(u => [u.id, u]))
            data.organization_members = data.organization_members.map(member => ({
              ...member,
              user_details: userDetailsMap.get(member.user_id) || null
            }))
          }
        } catch (userError) {
          // If RPC doesn't exist or fails, continue without user details
          console.warn('Could not fetch user details:', userError)
        }
      }
    }

    return data
  } catch (error) {
    console.error('Error fetching organization details:', error)
    throw error
  }
}

/**
 * Update member role (with master admin privileges)
 */
export const updateMemberRole = async (organizationId, memberId, newRole) => {
  try {
    const { updateMember } = await import('./organization')
    return await updateMember(organizationId, memberId, { role: newRole }, true)
  } catch (error) {
    console.error('Error updating member role:', error)
    throw error
  }
}

/**
 * Update organization subscription tier
 */
export const updateSubscriptionTier = async (organizationId, tier, trialEndsAt = null) => {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    
    // Update organization
    const { data: orgData, error: orgError } = await supabase
      .from('organizations')
      .update({
        subscription_tier: tier,
        trial_ends_at: trialEndsAt,
        updated_at: new Date().toISOString(),
      })
      .eq('id', organizationId)
      .select()
      .single()

    if (orgError) throw orgError

    // Update or create subscription record
    const { data: existingSub } = await supabase
      .from('subscriptions')
      .select('id')
      .eq('organization_id', organizationId)
      .single()

    if (existingSub) {
      const { error: subError } = await supabase
        .from('subscriptions')
        .update({
          tier,
          status: tier === 'demo' ? 'expired' : 'active',
          ends_at: trialEndsAt,
        })
        .eq('id', existingSub.id)

      if (subError) throw subError
    } else {
      const { error: subError } = await supabase
        .from('subscriptions')
        .insert({
          organization_id: organizationId,
          tier,
          status: tier === 'demo' ? 'expired' : 'active',
          ends_at: trialEndsAt,
        })

      if (subError) throw subError
    }

    // Log activity
    await supabase.rpc('log_platform_activity', {
      action_type_param: 'subscription_update',
      target_org_id: organizationId,
      details_param: {
        tier,
        trial_ends_at: trialEndsAt,
        updated_by: user?.id,
      },
    })

    return orgData
  } catch (error) {
    console.error('Error updating subscription tier:', error)
    throw error
  }
}

/**
 * Update billing cycle for an organization
 * Changes take effect at the end of the current billing period
 */
export const updateBillingCycle = async (organizationId, newCycle) => {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('User not authenticated')

    // Import billing cycle service
    const { updateBillingCycle: updateCycle } = await import('./billingCycleService')
    
    const result = await updateCycle(organizationId, newCycle)
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to update billing cycle')
    }

    // Log activity
    await supabase.rpc('log_platform_activity', {
      action_type_param: 'billing_cycle_update',
      target_org_id: organizationId,
      details_param: {
        new_cycle: newCycle,
        updated_by: user?.id,
      },
    }).catch(err => {
      // Log activity failure shouldn't break the update
      console.warn('Failed to log billing cycle update activity:', err)
    })

    return result
  } catch (error) {
    console.error('Error updating billing cycle:', error)
    throw error
  }
}

/**
 * Get platform analytics
 */
export const getPlatformAnalytics = async () => {
  try {
    // Get real-time counts
    const [
      { count: orgCount },
      { count: userCount },
      { count: clientCount },
      { count: examCount },
      { count: planCount },
      { data: organizations },
    ] = await Promise.all([
      supabase.from('organizations').select('*', { count: 'exact', head: true }),
      supabase.from('organization_members').select('*', { count: 'exact', head: true }),
      supabase.from('clients').select('*', { count: 'exact', head: true }),
      supabase.from('examinations').select('*', { count: 'exact', head: true }),
      supabase.from('care_plans').select('*', { count: 'exact', head: true }),
      supabase.from('organizations').select('subscription_tier'),
    ])

    // Count subscriptions by tier from organizations table
    const subscriptionStats = {
      total: organizations?.length || 0,
      active: organizations?.length || 0, // All organizations are considered active
      lite: organizations?.filter((o) => o.subscription_tier === 'lite').length || 0,
      essential: organizations?.filter((o) => o.subscription_tier === 'essential').length || 0,
      professional: organizations?.filter((o) => o.subscription_tier === 'professional').length || 0,
      enterprise: organizations?.filter((o) => o.subscription_tier === 'enterprise' || o.subscription_tier === 'growth').length || 0,
      white_label: organizations?.filter((o) => o.subscription_tier === 'white_label').length || 0,
      demo: organizations?.filter((o) => o.subscription_tier === 'demo').length || 0,
    }

    return {
      organizations: orgCount || 0,
      users: userCount || 0,
      clients: clientCount || 0,
      examinations: examCount || 0,
      carePlans: planCount || 0,
      subscriptions: subscriptionStats,
    }
  } catch (error) {
    console.error('Error fetching platform analytics:', error)
    throw error
  }
}

/**
 * Get activity logs
 */
export const getActivityLogs = async (limit = 100) => {
  try {
    // master_admin_id references auth.users(id), not a public table
    // So we can't join it directly. Just select the fields we need.
    const { data, error } = await supabase
      .from('platform_activity_log')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit)

    if (error) throw error
    return data || []
  } catch (error) {
    console.error('Error fetching activity logs:', error)
    throw error
  }
}

/**
 * Get recent organizations (for dashboard)
 */
export const getRecentOrganizations = async (limit = 10) => {
  try {
    const { data, error } = await supabase
      .from('organizations')
      .select(`
        *,
        subscriptions (*),
        organization_members (count)
      `)
      .order('created_at', { ascending: false })
      .limit(limit)

    if (error) throw error
    return data || []
  } catch (error) {
    console.error('Error fetching recent organizations:', error)
    throw error
  }
}

